import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp, faAnglesDown, faAnglesUp, faArrowDown, faArrowDownLong, faArrowUp, faArrowUpLong, faEquals, faSave } from "@fortawesome/free-solid-svg-icons";

import {
    ALPH_TOKEN_ID,
    DUST_AMOUNT,
    ONE_ALPH,
    convertAlphAmountWithDecimals,
    prettifyAttoAlphAmount,
    prettifyTokenAmount,
    web3,
} from "@alephium/web3";
import { useConnect, useWallet } from "@alephium/web3-react";

import { balanceOf, getContractConfig } from "src/services/utils";
import { useAppContext } from "src/context/AppContext";
import { faChartLine } from "@fortawesome/free-solid-svg-icons";
import MergeProposalVoteCard from "./MergeProposalVoteCard";
import { Vote } from "src/artifacts/ts";
import VotesList from "./VotesList";


const RocketRangeCurrentRound: React.FC = ({ }) => {
    web3.setCurrentNodeProvider(process.env.REACT_APP_NODE_URL ? process.env.REACT_APP_NODE_URL : "https://node.mainnet.alephium.org");
    const { connectionStatus, nodeProvider, account, signer } = useWallet()
    const { connect } = useConnect()
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(true);

    const { showPendingTX } = useAppContext();
    const [roundId, setRoundId] = React.useState<bigint>(0n);
    const [selectedOption, setSelectedOption] = React.useState<number | null>(null);
    const [betAmount, setBetAmount] = React.useState<number>(0);
    const [alphLastPrice, setAlphLastPrice] = useState<number>(0);
    const [countdown, setCountdown] = useState<string>("00H:00M");
    const [availableAlph, setAvailableAlph] = useState(0n);
    const [tradingViewVisible, setTradingViewVisible] = useState(false);

    const [userTier, setUserTier] = useState<string | null | undefined>(null);
    const [eligible, setEligible] = useState(false);
    const [votingOpen, setVotingOpen] = useState(false);
    const [votingStartsAt, setVotingStartsAt] = useState("");
    const [currentUserVote, setCurrentUserVote] = useState<null | string | undefined>(null);

    const [votes, setVotes] = useState<any>();

    const fetchData = async () => {
        const eventsResult = await fetchEvents();
        const remapped = remapVotes(eventsResult);
        setVotes(remapped);
        if (account) {
            const subaccount = (await Vote.at(getContractConfig("Vote").address).view.getSubAccount({ args: { caller: account.address } })).returns;
            try {
                const staked = (await Vote.at(getContractConfig("Vote").address).view.getStaked({ args: { subAccount: subaccount } })).returns;
                if (staked > 9999n * ONE_ALPH) setEligible(true)
                if (staked > 999999n * ONE_ALPH) setUserTier("diamond");
                else if (staked > 399999n * ONE_ALPH) setUserTier("platinum");
                else if (staked > 149999n * ONE_ALPH) setUserTier("gold");
                else if (staked > 39999n * ONE_ALPH) setUserTier("silver");
                else if (staked > 9999n * ONE_ALPH) setUserTier("bronze");
                else setUserTier(null);
            } catch (e) {
                console.log(e);
            }
            setCurrentUserVote(remapped.votes.find(x => x.address == account.address)?.decision);
        }
        setLoading(false);
    }

    function remapVotes(data) {
        console.log(data);
        const seen = new Set();
        const latestMap = new Map();
        let yesCount = 0;
        let noCount = 0;
        let yesPoints = 0;
        let noPoints = 0;

        // Sort latest first so we pick the most recent per address
        data
            .slice()
            .sort((a, b) => b.timestamp - a.timestamp)
            .forEach(e => {
                const address = e.fields[0].value;
                if (seen.has(address)) return;

                seen.add(address);
                latestMap.set(address, e);

                const voteChoice = Boolean(e.fields[1].value);
                const points = Number(e.fields[2].value);

                if (voteChoice) {
                    yesCount++;
                    yesPoints += points;
                } else {
                    noCount++;
                    noPoints += points;
                }
            });

        // Now build votes list in ascending time order for display
        const votes = Array.from(latestMap.values())
            .sort((a, b) => b.timestamp - a.timestamp)
            .map(e => {
                const address = e.fields[0].value;
                const voteChoice = Boolean(e.fields[1].value);
                const points = Number(e.fields[2].value);
                const decision = voteChoice ? "yes" : "no";

                return {
                    address,
                    voteChoice: String(voteChoice),
                    decision,
                    time: new Date(e.timestamp).toISOString(),
                    points: String(points)
                };
            });

        return {
            votes,
            totals: {
                yes: String(yesCount),
                no: String(noCount),
                yesPoints: String(yesPoints),
                noPoints: String(noPoints)
            }
        };
    }



    useEffect(() => {
        setLoading(true);
        // Initial fetch
        fetchData();
    }, [account]);

    const vote = async function (choice: string) {
        if (signer) {
            const tx = (await Vote.at(getContractConfig("Vote").address).transact.vote({ args: { voteOption: choice == "yes" ? true : false }, signer }))
            showPendingTX(tx.txId);
        }
    }

    const fetchEvents = async () => {
        const baseUrl = "https://backend.mainnet.alephium.org/contract-events/contract-address/" + getContractConfig("Vote").address;
        const eventIndex = 5;
        const limit = 100;
        let fetchedEvents: any[] = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
            const response = await fetch(`${baseUrl}/?event_index=${eventIndex}&page=${page}&limit=${limit}`);
            const data = await response.json();

            fetchedEvents = [...fetchedEvents, ...data];

            // If the number of events is less than the limit, stop fetching
            hasMore = data.length === limit;

            if (hasMore) {
                // Pause for 3 seconds before the next request
                await new Promise((resolve) => setTimeout(resolve, 500));
            }

            page++;
        }

        return fetchedEvents;
    };

    if (loading || !votes) return <></>
    // if (Number(gameData.betCloseTime) < (new Date().getTime())) {
    //     return (
    //         <div className="p-6 w-full mx-auto">
    //             {/* Title */}
    //             <h2 className="text-2xl font-bold text-white mb-4">Round #{gameData.gameId + 1n + ""}</h2>

    //             {/* Description */}
    //             <p className="text-gray-300 mb-6">Round not started</p>

    //             {/* Button */}
    //             {account ?
    //                 <button
    //                     onClick={createRound}
    //                     className="px-6 py-2 bg-red-600 text-white font-semibold rounded-md shadow-md hover:bg-red-700 transition duration-300"
    //                 >
    //                     Create Round
    //                 </button>
    //                 :
    //                 <p className="text-red-500 text-xl my-6 text-center"><strong>Connect wallet to proceed.</strong></p>
    //             }
    //         </div>

    //     );
    // }
    return (
        <>
            <MergeProposalVoteCard account={account?.address} currentUserVote={currentUserVote} eligible={eligible} userTier={userTier} votingOpen={votingOpen} onCastVote={vote} votingStartsAt={votingStartsAt} ></MergeProposalVoteCard>
            <VotesList loading={loading} votes={votes} />
        </>
    );
};

export default RocketRangeCurrentRound;
